import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/reducers';
import { actionCreators } from '../../../redux/middleware/ProviderConsumptionImport.Middleware';
import { actionCreators as importJobsActions } from '../../../redux/middleware/ImportJobs.Middleware';
import * as AzureFileUploadStore from '../../../redux/middleware/AzureFileUpload.Middleware';
import { buildProviderFileTarget } from '../../../functions/AzureFileUpload.Functions';

// models
import {
  ProviderFileTypeEnum,
  ProviderFileViewModel,
  RegisteredAzureFile,
} from '../../../models/Entities/AzureFiles/AzureFileUpload';
import { ErrorType } from '../../../models/HttpError';
import {
  DeleteImportedConsumptionsResult,
  GenerateProtocolConsumptionsResult,
  ImportedConsumptionsFilter,
  ImportedConsumptionsPage,
  ImportedProviderConsumption,
  ImportPreview,
  ImportProviderFileConsumptionsRequest,
  ImportStartResponse,
  PreviewItemIds,
  PreviewItemsFilter,
  PreviewItemsPage,
  PreviewItemsQuery,
  ProviderImportJob,
  ProviderConsumptionAssignmentRequest,
  ProtocolReferenceTemplate,
  ProviderMedicalServiceMapping,
  SaveReferenceTemplateRequest,
  ServiceMappingsPage,
  ServiceMappingsQuery,
  TemplateTestRequest,
  TemplateTestResult,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';

export interface ImportedConsumptionsOwnProps {
  /** Provider.id (int): archivos del proveedor e importación. */
  providerId: number;
  /** Provider.person.personIdentifier (Guid): consumos del proveedor. */
  providerIdentifier: string | undefined;
  providerName: string;
}

/** Thunks de la importación (todos devuelven promesas que rechazan con ErrorType). */
export interface ProviderConsumptionImportDispatch {
  LoadImportableProviderFiles: (providerId: number) => Promise<ProviderFileViewModel[]>;
  PreviewFileImport: (providerId: number, fileId: string | number, body: ImportProviderFileConsumptionsRequest) => Promise<ImportPreview | undefined>;
  ImportFileConsumptions: (
    providerId: number,
    fileId: string | number,
    body: ImportProviderFileConsumptionsRequest
  ) => Promise<ImportStartResponse>;
  DeleteImportedConsumptionsOfFile: (providerId: number, fileId: string | number) => Promise<DeleteImportedConsumptionsResult>;
  // ---- vista previa congelada (spec 009, MP 031) ----
  /** GET protocols/providers/{int}/consumptions/import-previews/{previewId}/items (404 = vencida o reemplazada). */
  LoadPreviewItems?: (providerId: number, previewId: string, query: PreviewItemsQuery) => Promise<PreviewItemsPage>;
  /** GET .../import-previews/{previewId}/item-ids (sólo seleccionables, con importes). */
  LoadPreviewItemIds?: (providerId: number, previewId: string, filter?: PreviewItemsFilter) => Promise<PreviewItemIds>;
  // ---- correspondencias de servicio del proveedor (spec 009, US6) ----
  /** GET protocols/providers/{int}/medical-service-mappings?search=&pageNumber=&pageSize= */
  LoadServiceMappings?: (providerId: number, filter: ServiceMappingsQuery) => Promise<ServiceMappingsPage>;
  /** PUT .../medical-service-mappings/{mappingId} { medicalServiceId } */
  UpdateServiceMapping?: (providerId: number, mappingId: string, medicalServiceId: number) => Promise<ProviderMedicalServiceMapping | undefined>;
  /** DELETE .../medical-service-mappings/{mappingId} */
  DeleteServiceMapping?: (providerId: number, mappingId: string) => Promise<void>;
  // ---- formatos de referencia de protocolo (spec 009, US5) ----
  /** GET protocols/providers/{int}/protocol-reference-templates */
  LoadReferenceTemplates?: (providerId: number) => Promise<ProtocolReferenceTemplate[]>;
  /** POST .../protocol-reference-templates { name, parts, sampleText } */
  CreateReferenceTemplate?: (providerId: number, body: SaveReferenceTemplateRequest) => Promise<ProtocolReferenceTemplate | undefined>;
  /** PUT .../protocol-reference-templates/{templateId} { name, parts, sampleText, active } */
  UpdateReferenceTemplate?: (providerId: number, templateId: string, body: SaveReferenceTemplateRequest) => Promise<ProtocolReferenceTemplate | undefined>;
  /** DELETE .../protocol-reference-templates/{templateId} */
  DeleteReferenceTemplate?: (providerId: number, templateId: string) => Promise<void>;
  /** PUT .../protocol-reference-templates/order { templateIds } */
  ReorderReferenceTemplates?: (providerId: number, templateIds: string[]) => Promise<ProtocolReferenceTemplate[]>;
  /** POST .../protocol-reference-templates/test { parts, texts } */
  TestReferenceTemplate?: (providerId: number, body: TemplateTestRequest) => Promise<TemplateTestResult[]>;
  LoadImportedConsumptions: (providerIdentifier: string, filter: ImportedConsumptionsFilter) => Promise<ImportedConsumptionsPage>;
  AssignProtocolAndPatient: (request: ProviderConsumptionAssignmentRequest) => Promise<ImportedProviderConsumption[]>;
  GenerateProtocolConsumptions: (ids: string[]) => Promise<GenerateProtocolConsumptionsResult>;
  LoadProtocolsForAssignment: () => Promise<Protocol[]>;
  LoadProtocolPatients: (protocolId: number) => Promise<ProtocolPatient[]>;
  /**
   * Links a storage file to the provider as "servicios realizados" (spec 005, US6):
   * POST protocols/providers/{ProviderId:int}/files/link { path, fileType: 2 }. Resolves with the new file id.
   */
  LinkServicesPerformedFile: (providerId: number, path: string) => Promise<string | undefined>;
  // ---- importación en segundo plano (spec 008) ----
  /** GET protocols/providers/consumptions/import-jobs/{jobId} (seguimiento del paso 3). */
  LoadImportJob: (jobId: string) => Promise<ProviderImportJob | undefined>;
  /** GET protocols/providers/{int}/consumptions/import-jobs?take= (historial y banner de la grilla). */
  LoadProviderImportJobs: (providerId: number, take?: number) => Promise<ProviderImportJob[]>;
  /** Registra el job en el aviso global (`notify` = avisar cuando termine). */
  TrackImportJob: (job: ProviderImportJob, notify?: boolean) => void;
  /** El asistente abierto muestra el job (su fin no lleva toast). */
  SetImportJobForeground: (jobId: string, foreground: boolean) => void;
  /** El fin del job ya se mostró (no se vuelve a avisar). */
  AcknowledgeImportJobNotice: (jobId: string) => void;
}

export type ImportedConsumptionsProps = ImportedConsumptionsOwnProps & ProviderConsumptionImportDispatch;

export const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): ProviderConsumptionImportDispatch => {
  const run = <T>(thunk: unknown): Promise<T> => dispatch(thunk as any) as unknown as Promise<T>;
  return {
    LoadImportableProviderFiles: (providerId) => run(actionCreators.LoadImportableProviderFiles(providerId)),
    PreviewFileImport: (providerId, fileId, body) => run(actionCreators.PreviewFileImport(providerId, fileId, body)),
    ImportFileConsumptions: (providerId, fileId, body) => run(actionCreators.ImportFileConsumptions(providerId, fileId, body)),
    DeleteImportedConsumptionsOfFile: (providerId, fileId) => run(actionCreators.DeleteImportedConsumptionsOfFile(providerId, fileId)),
    LoadPreviewItems: (providerId, previewId, query) => run(actionCreators.LoadPreviewItems(providerId, previewId, query)),
    LoadPreviewItemIds: (providerId, previewId, filter) => run(actionCreators.LoadPreviewItemIds(providerId, previewId, filter)),
    LoadServiceMappings: (providerId, filter) => run(actionCreators.LoadServiceMappings(providerId, filter)),
    UpdateServiceMapping: (providerId, mappingId, medicalServiceId) => run(actionCreators.UpdateServiceMapping(providerId, mappingId, medicalServiceId)),
    DeleteServiceMapping: (providerId, mappingId) => run(actionCreators.DeleteServiceMapping(providerId, mappingId)),
    LoadReferenceTemplates: (providerId) => run(actionCreators.LoadReferenceTemplates(providerId)),
    CreateReferenceTemplate: (providerId, body) => run(actionCreators.CreateReferenceTemplate(providerId, body)),
    UpdateReferenceTemplate: (providerId, templateId, body) => run(actionCreators.UpdateReferenceTemplate(providerId, templateId, body)),
    DeleteReferenceTemplate: (providerId, templateId) => run(actionCreators.DeleteReferenceTemplate(providerId, templateId)),
    ReorderReferenceTemplates: (providerId, templateIds) => run(actionCreators.ReorderReferenceTemplates(providerId, templateIds)),
    TestReferenceTemplate: (providerId, body) => run(actionCreators.TestReferenceTemplate(providerId, body)),
    LoadImportedConsumptions: (providerIdentifier, filter) => run(actionCreators.LoadImportedConsumptions(providerIdentifier, filter)),
    AssignProtocolAndPatient: (request) => run(actionCreators.AssignProtocolAndPatient(request)),
    GenerateProtocolConsumptions: (ids) => run(actionCreators.GenerateProtocolConsumptions(ids)),
    LoadProtocolsForAssignment: () => run(actionCreators.LoadProtocolsForAssignment()),
    LoadProtocolPatients: (protocolId) => run(actionCreators.LoadProtocolPatients(protocolId)),
    LinkServicesPerformedFile: async (providerId, path) => {
      const target = buildProviderFileTarget(providerId);
      if (!target) throw { ErrorCode: 400, ErrorMessage: 'Invalid provider', Errors: [] } as ErrorType;
      const file = await run<RegisteredAzureFile | undefined>(
        AzureFileUploadStore.ActionCreators.LinkAzureFile('provider', target, path, ProviderFileTypeEnum.ServicesPerformed)
      );
      return file?.id;
    },
    LoadImportJob: (jobId) => run(importJobsActions.LoadImportJob(jobId)),
    LoadProviderImportJobs: (providerId, take) => run(importJobsActions.LoadProviderImportJobs(providerId, take)),
    TrackImportJob: (job, notify = true) => {
      dispatch(importJobsActions.TrackImportJob(job, notify) as any);
    },
    SetImportJobForeground: (jobId, foreground) => {
      dispatch(importJobsActions.SetImportJobForeground(jobId, foreground) as any);
    },
    AcknowledgeImportJobNotice: (jobId) => {
      dispatch(importJobsActions.AcknowledgeImportJobNotice(jobId) as any);
    },
  };
};
